import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  Clear as ClearIcon,
  LaptopMac as WebIcon,
  PhoneAndroid as AndroidIcon,
  PhoneIphone as IosIcon,
  Smartphone as AppIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { BarChart as MuiBarChart } from "@mui/x-charts/BarChart";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { AnalyticsServices } from "./AnalyticsServices";
import Swal from "sweetalert2";

export default function Analytics() {
  const menuDispatch = useDispatch();
  
  // Highlight sidebar item
  useEffect(() => {
    menuDispatch({
      type: "menuSelect",
      payload: "analytics",
    });
  }, [menuDispatch]);

  // States
  const [activeTab, setActiveTab] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [recordsData, setRecordsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Platform overall aggregation state
  const [platformStats, setPlatformStats] = useState([]);
  const [loadingPlatformStats, setLoadingPlatformStats] = useState(false);

  // Filter States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupBy, setGroupBy] = useState("source");
  const [utmSource, setUtmSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");

  // Pagination State for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Fetch UTM Summary
  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await AnalyticsServices.getUtmSummary({
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy,
      });
      if (res.success === 1) {
        setSummaryData(res.data);
      } else {
        Swal.fire("Error", res.message || "Failed to load summary", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error while fetching summary", "error");
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch Platform Overall Stats
  const fetchPlatformStats = async () => {
    try {
      setLoadingPlatformStats(true);
      const res = await AnalyticsServices.getUtmSummary({
        start_date: startDate,
        end_date: endDate,
        group_by: "platform",
      });
      if (res.success === 1) {
        setPlatformStats(res.data);
      }
    } catch (err) {
      console.error("Error fetching platform stats:", err);
    } finally {
      setLoadingPlatformStats(false);
    }
  };

  // Fetch UTM Records
  const fetchRecords = async () => {
    try {
      setLoadingRecords(true);
      const res = await AnalyticsServices.getUtmRecords({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        user_id: userIdFilter,
        start_date: startDate,
        end_date: endDate,
        platform: platformFilter,
      });
      if (res.success === 1) {
        setRecordsData(res.data);
        setTotalRecords(res.pagination.total);
      } else {
        Swal.fire("Error", res.message || "Failed to load records", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error while fetching records", "error");
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchPlatformStats();
  }, [groupBy, startDate, endDate]);

  useEffect(() => {
    fetchRecords();
  }, [paginationModel.page, paginationModel.pageSize, startDate, endDate, platformFilter]);

  const handleApplyFilters = () => {
    fetchSummary();
    fetchPlatformStats();
    fetchRecords();
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setUtmSource("");
    setUtmCampaign("");
    setUtmMedium("");
    setUserIdFilter("");
    setPlatformFilter("");
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  // Compute platform details from stats
  const webStats = platformStats.find(item => item.platform === "web") || { total_clicks: 0, unique_users: 0 };
  const androidStats = platformStats.find(item => item.platform === "android") || { total_clicks: 0, unique_users: 0 };
  const iosStats = platformStats.find(item => item.platform === "ios") || { total_clicks: 0, unique_users: 0 };
  
  // App is Android + iOS
  const appClicks = androidStats.total_clicks + iosStats.total_clicks;
  const appUniqueUsers = androidStats.unique_users + iosStats.unique_users;

  const totalClicks = summaryData.reduce((acc, curr) => acc + curr.total_clicks, 0);
  const totalUniqueUsers = summaryData.reduce((acc, curr) => acc + curr.unique_users, 0);

  // DataGrid Columns for Records
  const columns = [
    { field: "clicked_at", headerName: "Clicked At", width: 180, renderCell: (params) => new Date(params.value).toLocaleString() },
    { field: "utm_source", headerName: "Source", width: 130 },
    { field: "utm_medium", headerName: "Medium", width: 120 },
    { field: "utm_campaign", headerName: "Campaign", width: 150 },
    { field: "deep_link_path", headerName: "Deep Link Path", width: 220 },
    { field: "platform", headerName: "Platform", width: 110 },
    { field: "device_type", headerName: "Device Type", width: 110 },
    { field: "device_model", headerName: "Device Model", width: 130 },
    { field: "user_id", headerName: "User ID", width: 100, renderCell: (params) => params.value || "Guest" },
    { field: "ip_address", headerName: "IP Address", width: 130 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <PageHeaderLayout headerTitle="UTM Deep Link Analytics">
        <Tooltip title="Refresh Data">
          <IconButton onClick={handleApplyFilters} color="primary" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </PageHeaderLayout>

      {/* Overview Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Card 1: Total Clicks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #3f51b5 0%, #1a237e 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: "0.75rem", fontWeight: "bold" }}>TOTAL CLICKS</Typography>
                <TrendingUpIcon fontSize="small" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {loadingSummary ? <CircularProgress size={24} color="inherit" /> : totalClicks}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8, fontSize: "0.7rem" }}>
                {totalUniqueUsers} Unique Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Card 2: Web Clicks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #0288d1 0%, #01579b 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: "0.75rem", fontWeight: "bold" }}>WEB CLICKS</Typography>
                <WebIcon fontSize="small" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {loadingPlatformStats ? <CircularProgress size={24} color="inherit" /> : webStats.total_clicks}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8, fontSize: "0.7rem" }}>
                {webStats.unique_users} Unique Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3: Total App Clicks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: "0.75rem", fontWeight: "bold" }}>TOTAL APP CLICKS</Typography>
                <AppIcon fontSize="small" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {loadingPlatformStats ? <CircularProgress size={24} color="inherit" /> : appClicks}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8, fontSize: "0.7rem" }}>
                {appUniqueUsers} Unique Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4: Android Clicks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #689f38 0%, #33691e 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: "0.75rem", fontWeight: "bold" }}>ANDROID CLICKS</Typography>
                <AndroidIcon fontSize="small" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {loadingPlatformStats ? <CircularProgress size={24} color="inherit" /> : androidStats.total_clicks}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8, fontSize: "0.7rem" }}>
                {androidStats.unique_users} Unique Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 5: iOS Clicks */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #78909c 0%, #37474f 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: "0.75rem", fontWeight: "bold" }}>IOS CLICKS</Typography>
                <IosIcon fontSize="small" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {loadingPlatformStats ? <CircularProgress size={24} color="inherit" /> : iosStats.total_clicks}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8, fontSize: "0.7rem" }}>
                {iosStats.unique_users} Unique Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Drawer / Container */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center" }}>
          <FilterIcon sx={{ mr: 1, fontSize: 20 }} /> Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="platform-filter-label">Platform</InputLabel>
              <Select
                labelId="platform-filter-label"
                value={platformFilter}
                label="Platform"
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                <MenuItem value="">All Platforms</MenuItem>
                <MenuItem value="web">Web</MenuItem>
                <MenuItem value="android">Android</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="UTM Source"
              fullWidth
              placeholder="e.g. facebook"
              value={utmSource}
              onChange={(e) => setUtmSource(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="UTM Campaign"
              fullWidth
              placeholder="e.g. summer_sale"
              value={utmCampaign}
              onChange={(e) => setUtmCampaign(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" color="primary" onClick={handleApplyFilters} fullWidth size="medium">
                Apply
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleClearFilters} size="medium">
                <ClearIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs Section */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} aria-label="analytics tabs">
          <Tab label="Performance Summary" sx={{ fontWeight: "bold" }} />
          <Tab label="Detailed Click Logs" sx={{ fontWeight: "bold" }} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {activeTab === 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)" }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>Clicks Breakdown</Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="group-by-label">Group By</InputLabel>
                <Select
                  labelId="group-by-label"
                  value={groupBy}
                  label="Group By"
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <MenuItem value="source">UTM Source</MenuItem>
                  <MenuItem value="medium">UTM Medium</MenuItem>
                  <MenuItem value="campaign">UTM Campaign</MenuItem>
                  <MenuItem value="path">Target Path</MenuItem>
                  <MenuItem value="platform">Platform</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Chart Section */}
            {!loadingSummary && summaryData.length > 0 && (
              <Box sx={{ mb: 4, mt: 2, display: "flex", justifyContent: "center", width: "100%" }}>
                <Box sx={{ width: "100%", maxWidth: 1000, height: 350 }}>
                  <MuiBarChart
                    dataset={summaryData.slice(0, 10).map((item) => ({
                      label: item[groupBy] || "(not set)",
                      clicks: item.total_clicks,
                      users: item.unique_users,
                    }))}
                    xAxis={[
                      {
                        scaleType: "band",
                        dataKey: "label",
                      },
                    ]}
                    series={[
                      { dataKey: "clicks", label: "Clicks", color: "#1976d2" },
                      { dataKey: "users", label: "Unique Users", color: "#2e7d32" },
                    ]}
                    height={300}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "middle" },
                        padding: 0,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {loadingSummary ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : summaryData.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="textSecondary">No analytics data found for the selected criteria.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ bgcolor: "action.hover" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {groupBy.toUpperCase()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">Total Clicks</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">Unique Users</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>First Clicked At</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Last Clicked At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontWeight: "medium" }}>
                          {row[groupBy] || <Typography variant="caption" color="textSecondary"><i>(not set)</i></Typography>}
                        </TableCell>
                        <TableCell align="right">{row.total_clicks}</TableCell>
                        <TableCell align="right">{row.unique_users}</TableCell>
                        <TableCell>{new Date(row.first_click).toLocaleString()}</TableCell>
                        <TableCell>{new Date(row.last_click).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Box sx={{ minWidth: 1500 }}>
                <DataGrid
                  rows={recordsData}
                  columns={columns}
                  getRowId={(row) => row._id}
                  paginationMode="server"
                  rowCount={totalRecords}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  loading={loadingRecords}
                  pageSizeOptions={[10, 25, 50]}
                  autoHeight
                  disableRowSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": { fontSize: "0.85rem" },
                    "& .MuiDataGrid-columnHeaders": { bgcolor: "action.hover", borderBottom: "1px solid rgba(224, 224, 224, 1)" },
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
